import { Button } from "@acme/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@acme/ui/sheet";
import { createFileRoute, Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, ArrowLeft, Loader2, Menu } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import EntityDetailTabs from "~/common/components/entity-detail-tabs.js";
import { ConnectToClanCard } from "~/features/clan-detail/_components/connect-to-clan-card.js";
import useClanDetailAccess from "~/features/clan-detail/_hooks/useClanDetailAccess.js";
import {
  getClanActiveTab,
  getFirstVisibleClanTabRoute,
  getVisibleClanTabs,
  isClanTabVisible,
} from "~/features/clan-detail/_utils/clan-tab-routes.js";
import { normalizeClanTag } from "~/features/search-filters/_utils/normalize-tag.js";
import { ClanEmblem } from "../../my-players/_components/-clan-emblem.js";

export const Route = createFileRoute("/_authed/my-clans/$tag")({
  component: ClanTagLayout,
});

function ClanTagLayout() {
  const { t } = useTranslation();
  const { tag } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);
  const clanTag = normalizeClanTag(tag);
  const access = useClanDetailAccess({ tag });

  const visibleTabs = useMemo(
    () => getVisibleClanTabs(access.effectivePermissions),
    [access.effectivePermissions]
  );
  const activeTab = getClanActiveTab(location.pathname, tag);

  useEffect(() => {
    if (!access.isReady) return;
    if (!access.canAccessClan) return;
    if (isClanTabVisible(activeTab, access.effectivePermissions)) return;

    void navigate({
      to: getFirstVisibleClanTabRoute(access.effectivePermissions),
      params: { tag },
      replace: true,
    });
  }, [access.canAccessClan, access.effectivePermissions, access.isReady, activeTab, navigate, tag]);

  const displayTag = clanTag;
  const hasAccess = access.isReady && access.canAccessClan;
  const noAccess = access.isReady && !access.canAccessClan;

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4 mb-6">
        <Link
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground shrink-0"
          to="/my-clans">
          <ArrowLeft className="h-4 w-4" />
          {t("app.clanDetail.backToClans")}
        </Link>
        {hasAccess && (
          <nav aria-label={t("app.clanDetail.sectionsAria")} className="flex items-center min-w-0">
            <div className="hidden md:block">
              <EntityDetailTabs activeTab={activeTab} tabs={visibleTabs} tag={tag} />
            </div>
            <Sheet onOpenChange={setSheetOpen} open={sheetOpen}>
              <SheetTrigger asChild>
                <Button
                  aria-label={t("app.clanDetail.openMenu")}
                  className="md:hidden shrink-0"
                  size="icon"
                  variant="ghost">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent className="w-[min(100vw-2rem,18rem)]" side="right">
                <SheetHeader>
                  <SheetTitle>{t("app.clanDetail.sectionsMenu")}</SheetTitle>
                </SheetHeader>
                <EntityDetailTabs
                  activeTab={activeTab}
                  className="pt-2"
                  layout="vertical"
                  onNavigate={() => setSheetOpen(false)}
                  tabs={visibleTabs}
                  tag={tag}
                />
              </SheetContent>
            </Sheet>
          </nav>
        )}
      </div>

      {access.clanInfo != null ? (
        <div className="mb-6 flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <ClanEmblem badgeUrls={access.clanInfo.badgeUrls ?? undefined} className="max-h-12" size="fill" />
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold truncate">{access.clanInfo.name}</h1>
              <p className="text-sm text-muted-foreground font-mono">{displayTag}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-muted-foreground">{displayTag}</h1>
        </div>
      )}

      {access.isSyncing && (
        <p className="mb-6 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {t("app.clanDetail.syncing")}
        </p>
      )}

      {access.syncFailed && (
        <p className="mb-6 flex items-center gap-2 text-sm text-destructive">
          <AlertTriangle className="size-4" />
          {t("app.clanDetail.syncFailed")}
        </p>
      )}

      {noAccess ? (
        <div className="space-y-4">
          <p className="text-muted-foreground">{t("app.clanDetail.noAccess")}</p>
          <ConnectToClanCard clanTag={displayTag} />
        </div>
      ) : (
        <Outlet />
      )}
    </div>
  );
}

export function useClanSearchAuth() {
  const { tag } = Route.useParams();
  const access = useClanDetailAccess({ tag });

  return {
    clanTag: access.clanTag,
    ownerTag: access.ownerTag,
    auth: access.auth,
    effectivePermissions: access.effectivePermissions,
    isReady: access.isReady,
    userIsOwner: access.userIsOwner,
    canAccessClan: access.canAccessClan,
  };
}
